import { useEffect } from "react";

/**
 * Custom hook to register a click event listener for elements with a specific selector
 * @param selector The CSS selector to listen for click events on
 * @param onElementClick Function to call when the selector is clicked
 */
export function useRegisterEventEmitter(
  selector: string,
  onElementClick: () => void,
) {
  useEffect(() => {
    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.closest(selector)) {
        onElementClick();
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [selector, onElementClick]);
}

/**
 * Custom hook to register a keyboard event listener for elements with a specific selector
 * @param selector The CSS selector to listen for keyboard events on
 * @param key The key to listen for (e.g., 'Enter')
 * @param onKeyPress Function to call when the key is pressed
 * @param modifiers Object with boolean flags for modifier keys
 */
export function useRegisterKeyEventEmitter(
  selector: string,
  key: string,
  onKeyPress: () => void,
  modifiers: { shift?: boolean; ctrl?: boolean; alt?: boolean } = {},
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest(selector) && event.key === key) {
        // Check modifiers
        if (modifiers.shift !== undefined && event.shiftKey !== modifiers.shift)
          return;
        if (modifiers.ctrl !== undefined && event.ctrlKey !== modifiers.ctrl)
          return;
        if (modifiers.alt !== undefined && event.altKey !== modifiers.alt)
          return;

        onKeyPress();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selector, key, onKeyPress, modifiers]);
}

/**
 * Custom hook specifically for the composer send button
 * @param onSendButtonClick Function to call when the send button is clicked
 */
export function useRegisterComposerSendButtonEvent(
  onSendButtonClick: () => void,
) {
  return useRegisterEventEmitter(".event-emit_send_button", onSendButtonClick);
}

/**
 * Custom hook for detecting Enter key press in the composer input field
 * @param onEnterKeyPress Function to call when Enter is pressed
 */
export function useRegisterComposerEnterKeyEvent(onEnterKeyPress: () => void) {
  return useRegisterKeyEventEmitter(
    ".event-emit_input_field",
    "Enter",
    onEnterKeyPress,
    { shift: false },
  );
}
